import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button, Flex, Select, Table, Tag, Tooltip, message, Spin } from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { SearchInput } from '@/components/SearchInput'

import Title from 'antd/es/typography/Title'
import type { Test } from '@/data'
import { getTests } from '@/api/lop/flow-experiments/getTests'
import { updateTest } from '@/api/lop/flow-experiments/updateTest'
import { deleteTest } from '@/api/lop/flow-experiments/deleteTest'
import { archiveFlowEvents } from '@/api/lop/flow-experiments/archiveFlowEvents'

import { getFunnelsURL } from '@/api/client'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'
import { resolveBackendSort } from '@/utils/backendSort'
import { getErrorMessage } from '@/utils/getErrorMessage'

import styles from './index.module.css'
import CopyIcon from './icons/copyIcon'
import ClearDBIcon from './icons/clearDBIcon'
import EditIcon from './icons/editIcon'
import ReportIcon from './icons/reportIcon'
import DeleteIcon from './icons/deleteIcon'
import ChanceToWin from './components/chanceToWin'
import Modal from './components/Modal'
import ConfirmModal from './components/ConfirmModal'

const metricColor = (val: string | number | null | undefined): string => {
  if (val == null) return '#4D4F53'
  const n = typeof val === 'string' ? parseFloat(val) : val
  if (isNaN(n)) return '#8c8c8c'
  return n >= 0 ? (n >= 5 ? '#2E7D32' : '#DF7A00') : '#C1121F'
}

const metricFMT = (val: string | number | null | undefined) => {
  return val && isNaN(parseInt(val?.toString())) ? '-' : `${val}%`
}

export const TestsList: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!
  const [tests, setTests] = useState<Test[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  })

  const [sortField, setSortField] = useState('-created_at')
  const [statusFilter, setStatusFilter] = useState<string>()
  const [search, setSearch] = useState('')
  const [modalData, setModalData] = useState<Test>()
  const [modalOpen, setModalOpen] = useState(false)

  const [confirmModalContent, setConfirmModalContent] =
    useState<React.ReactNode | null>(null)

  const hasFetched = useRef(false)
  const canReadTests = hasPermission(auth.user, 'lop:ui:action:read:tests')
  const canCreateTests = hasPermission(auth.user, 'lop:ui:action:create:tests')
  const canUpdateTests = hasPermission(auth.user, 'lop:ui:action:update:tests')

  const columnSortKeys = {
    name: 'name',
    status: 'status',
    created_at: 'created_at',
  } as const

  const fetchTests = useCallback(
    async (q: {
      page: number
      pageSize: number
      sort: string
      status: string | undefined
      search: string
    }) => {
      const modalAbortController = new AbortController()
      let testData: Test[] = []
      try {
        setLoading(true)
        setError(null)
        const filters: { key: string; value: string }[] = []
        if (q.status) filters.push({ key: 'status', value: q.status })
        if (q.search) filters.push({ key: 'name', value: q.search })
        const { data, meta } = await getTests({
          page: q.page,
          per_page: q.pageSize,
          include: 'metrics',
          sort: q.sort,
          ...(filters.length ? { filters } : {}),
        })
        setTests(data)
        testData = data
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch (err) {
        setError('Failed to load tests')
        console.error(err)
      } finally {
        //recalculate table

        window.setTimeout(async () => {
          document
            .querySelectorAll('.ant-table-tbody > tr > td:nth-of-type(2)')
            .forEach((tableCell) => {
              tableCell.setAttribute('colspan', '8')
              tableCell.setAttribute('style', 'padding:0;')
            })

          if (
            document.querySelectorAll(
              '.ant-table-tbody > tr > td:nth-of-type(n+3):not(:nth-of-type(n+9))',
            ).length > 16
          ) {
            //remove extraneous cells - thanks Ant!
            document
              .querySelectorAll(
                '.ant-table-tbody > tr > td:nth-of-type(n+3):not(:nth-of-type(n+9))',
              )
              .forEach((cell) => {
                cell.remove()
              })
          }

          const headers = document.querySelectorAll(
            '.ant-table-content thead th:nth-of-type(n+1):not(:nth-of-type(n+9))',
          )
          const headerSizes = Array.from(headers).map(
            // @ts-expect-error - offsetWidth doesn't exist on Element, can't cast to HTMLElement
            (header) => header.offsetWidth,
          )

          const childTables = document.querySelectorAll('.auto-adjust-table')

          childTables.forEach((table) => {
            const rows = table.querySelectorAll('tr')
            rows.forEach((row, rowIdx) => {
              row.classList.add(styles['auto-adjust-row'])
              row.querySelectorAll('td').forEach((cell, cellIdx) => {
                //cell 0 should be header 0 + header 1 in width, corrected for the 2px combined borders
                cell.setAttribute('width', headerSizes[0] + headerSizes[1])

                if (cellIdx > 0) {
                  cell.setAttribute('width', headerSizes[cellIdx + 1])
                }
              })
              if (rowIdx % 2 == 1) {
                row.setAttribute('style', 'background-color:#F5F5F5')
              }
            })
          })

          //psuedo-header rows
          await Array.from(document.querySelectorAll('.pseudo-header')).forEach(
            (psuedoheader) => {
              psuedoheader.remove()
            },
          )

          const replacementCells = document.querySelectorAll(
            '.ant-table-tbody > tr >td:nth-of-type(1)',
          )
          const contentRows = document.querySelectorAll('.ant-table-tbody > tr')

          const antTable = document.querySelector('.ant-table-tbody')
          if (!antTable) return

          replacementCells.forEach((rc, idx) => {
            const newRow = document.createElement('tr')
            newRow.setAttribute('key', `auto-adjust-table-${idx}`)
            const headerCell = document.createElement('td')
            newRow.classList.add('pseudo-header')
            newRow.classList.add(styles['pseudo-header-style'])
            headerCell.setAttribute('colspan', '14')

            headerCell.innerHTML = rc.innerHTML

            newRow.appendChild(headerCell)

            antTable.insertBefore(newRow, contentRows[idx])
            rc.setAttribute('style', 'display:none')
          })

          contentRows.forEach((contentRow) => {
            const firstID = contentRow
              .querySelector('[data-flow-id]')
              ?.getAttribute('data-flow-id')
            if (firstID) {
              contentRow.setAttribute('data-flow-id', firstID)
            }
          })
        }, 100)

        const triggerRows = document.querySelectorAll('.ant-table-tbody')
        triggerRows.forEach((triggerRow) => {
          triggerRow.addEventListener(
            'click',
            (evt) => {
              populateAndShowModal(evt as MouseEvent, testData)
            },
            modalAbortController,
          )
        })

        setLoading(false)
      }
      return () => {
        modalAbortController.abort()
      }
    },
    [],
  )

  const populateAndShowModal = function (evt: MouseEvent, data: Test[]) {
    if (evt.target === null) return
    const target = evt.target as HTMLElement
    let targettedDataSet = null
    //@ts-expect-error - target will ALWAYS be an HTML Node and will ALWAYS have a parentNode
    if (target.parentNode.tagName === 'TR') {
      // subtable
      //@ts-expect-error - target will ALWAYS be an HTML Node and will ALWAYS have a parentNode
      const targetChildId = target.parentNode.getAttribute('data-flow-id')
      targettedDataSet = data.filter((datum) => {
        return [
          //@ts-expect-error - datum will always have a base_flow, this is a typescript definition error
          { id: datum.base_flow.id, flow: datum.base_flow },
          ...datum.flows,
        ].filter((childDatum) => {
          return childDatum.id === parseInt(targetChildId)
        }).length
      })[0]
    } else if (target.classList.contains('js-psuedo-header')) {
      // psuedo header
      const targetId = target.getAttribute('data-id')
      targettedDataSet = data.filter((datum) => {
        return datum.id == targetId
      })[0]
    }

    if (targettedDataSet === null) {
      return
    }

    setModalData(targettedDataSet)
    setModalOpen(true)
  }

  const renderConfirmModal = (
    confirmMessage: string,
    successCallback: () => Promise<void>,
  ) => {
    const modalCloseCallback = () => {
      setConfirmModalContent(null)
    }

    setConfirmModalContent(
      <>
        <ConfirmModal
          modalCloseCallback={modalCloseCallback}
          successCallback={async () => {
            await successCallback()
            setConfirmModalContent(null)
          }}
        >
          {confirmMessage}
        </ConfirmModal>
      </>,
    )
  }

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
      fetchTests({
        page: 1,
        pageSize: pagination.pageSize,
        sort: sortField,
        status: statusFilter,
        search: value,
      })
    },
    [fetchTests, pagination.pageSize, sortField, statusFilter],
  )

  useEffect(() => {
    if (hasFetched.current) return
    if (!canReadTests) return
    hasFetched.current = true
    fetchTests({
      page: 1,
      pageSize: pagination.pageSize,
      sort: sortField,
      status: statusFilter,
      search,
    })
  }, [
    canReadTests,
    hasFetched,
    fetchTests,
    pagination.pageSize,
    sortField,
    statusFilter,
    search,
  ])

  const handleArchive = async (id: string) => {
    try {
      await updateTest(id, { status: 'archived' })
      setTests((prev) =>
        prev.map((t) => (t.id === id ? { ...t, status: 'archived' } : t)),
      )
      message.success('Test archived')
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to archive test'))
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteTest(id)
      setTests((prev) => prev.filter((t) => t.id !== id))
      message.success('Test deleted')
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to delete test'))
    }
  }

  const handleArchiveEvents = async (id: string) => {
    try {
      await archiveFlowEvents(id)
      message.success('Data Cleared')
      fetchTests({
        page: 1,
        pageSize: pagination.pageSize,
        sort: sortField,
        status: statusFilter,
        search,
      })
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to clear events'))
    }
  }

  const columns: TableProps<Test>['columns'] = [
    {
      title: 'Test Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text, record) => {
        return canUpdateTests ? (
          <div data-id={record.id} className={`js-psuedo-header`}>
            <Link to={`/tests/${record.id}/edit`}>{text}</Link>
          </div>
        ) : (
          text
        )
      },
    },
    {
      title: 'Lead Flows',
      key: 'lead_flows',
      minWidth: 180,
      render: (_, record) => {
        const copiedBaseFlow = {
          id: record.base_flow && record.base_flow.id ? record.base_flow.id : 0,
          flow: record.base_flow,
          isControl: true,
        }
        const variants = record.flows
          ? [copiedBaseFlow, ...record.flows]
          : [copiedBaseFlow]
        if (variants.length === 0 && !record.base_flow) return '-'

        return (
          <table className="auto-adjust-table">
            <tbody>
              {variants &&
                variants.map((variant, rowIdx) => {
                  let consumedTraffic = 0
                  if (Object.keys(variant).includes('isControl')) {
                    consumedTraffic = variants
                      .map((vnt) => {
                        //@ts-expect-error - needs some cast magic
                        return vnt.traffic_percentage
                      })
                      .filter((traffic_percentage) => {
                        return typeof traffic_percentage !== 'undefined'
                      })
                      .reduce(
                        (accumulator, currentValue) =>
                          accumulator + currentValue,
                        0,
                      )
                  }
                  return (
                    <tr data-flow-id={variant.id} key={rowIdx}>
                      <td className={`${styles.autoAdjustCell}`}>
                        <Link
                          to={`${getFunnelsURL()}/t/${variant.flow && variant.flow.template_slug}/?flow=${variant.flow && variant.flow.slug}&skiptesting=true&include=brand`}
                          target="_blank"
                        >
                          {variant.flow && variant.flow.name}
                        </Link>
                      </td>
                      <td
                        className={`${styles.autoAdjustCell} ${styles['autoAdjustCell--center']}`}
                      >
                        {
                          //@ts-expect-error - needs some cast magic
                          variant.traffic_percentage
                            ? //@ts-expect-error - needs some cast magic
                              variant.traffic_percentage
                            : 100 - consumedTraffic
                        }
                        %
                      </td>
                      <td
                        className={`${styles.autoAdjustCell} ${styles['autoAdjustCell--center']}`}
                      >
                        {variant.flow?.metrics?.visitor_count}
                      </td>
                      <td
                        className={`${styles.autoAdjustCell} ${styles['autoAdjustCell--center']}`}
                      >
                        <span
                          style={{
                            color: metricColor(variant.flow?.metrics?.growth),
                          }}
                        >
                          {metricFMT(variant.flow?.metrics?.growth)}
                        </span>
                      </td>
                      <td
                        className={`${styles.autoAdjustCell} ${styles['autoAdjustCell--center']}`}
                      >
                        <span
                          style={{
                            color: metricColor(variant.flow?.metrics?.ltc),
                          }}
                        >
                          {metricFMT(variant.flow?.metrics?.ltc)}
                        </span>
                      </td>
                      <td
                        className={`${styles.autoAdjustCell} ${styles['autoAdjustCell--center']}`}
                      >
                        {variant.flow?.metrics?.enrollments || '-'}
                      </td>
                      <td
                        className={`${styles.autoAdjustCell} ${styles['autoAdjustCell--center']}`}
                      >
                        {rowIdx === 0 && <>Control</>}
                        {rowIdx > 0 && (
                          <>
                            <ChanceToWin
                              chance={variant.flow?.metrics?.chance_to_win}
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        )
      },
    },
    {
      title: 'Traffic %',
    },
    {
      title: 'Visitors',
    },
    {
      title: 'Growth',
    },
    {
      title: 'LTC Δ',
    },
    {
      title: 'Enrollment Δ',
    },
    {
      title: 'Chance to Win',
    },
    {
      title: 'Visitors',
      key: 'lead_count',
      sorter: false,
      render: (_, record) => {
        const toNum = (v: unknown) => {
          const n = Number(v)
          return Number.isFinite(n) ? n : 0
        }
        const base = toNum(record.base_flow?.metrics?.visitor_count)
        const variants = (record.flows ?? []).reduce(
          (sum, v) => sum + toNum(v.flow?.metrics?.visitor_count),
          0,
        )
        const total = base + variants
        return total.toLocaleString()
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Paused', value: 'paused' },
        { text: 'Archived', value: 'archived' },
      ],
      filterMultiple: false,
      render: (status: Test['status'], record) => {
        if (!canUpdateTests) {
          if (!status) return '—'
          const colorMap = {
            active: 'green',
            paused: 'orange',
            archived: 'default',
          }
          const labelMap = {
            active: 'Active',
            paused: 'Paused',
            archived: 'Archived',
          }
          return <Tag color={colorMap[status]}>{labelMap[status]}</Tag>
        }
        return (
          <Select
            value={status ?? 'paused'}
            size="small"
            variant="borderless"
            style={{ width: 95 }}
            onChange={async (value) => {
              try {
                await updateTest(record.id, { status: value })
                setTests((prev) =>
                  prev.map((t) =>
                    t.id === record.id ? { ...t, status: value } : t,
                  ),
                )
                message.success('Status updated')
              } catch (err) {
                message.error(getErrorMessage(err, 'Failed to update status'))
              }
            }}
            options={[
              { value: 'active', label: <Tag color="green">Active</Tag> },
              { value: 'paused', label: <Tag color="orange">Paused</Tag> },
              { value: 'archived', label: <Tag color="default">Archived</Tag> },
            ]}
          />
        )
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (value: string) =>
        value
          ? new Date(value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Flex gap={12}>
          <Tooltip title="Duplicate test">
            <Link to={`/tests/${record.id}/edit`}>
              <CopyIcon style={{ fontSize: 16, color: 'white' }} />
            </Link>
          </Tooltip>
          <Tooltip title="Clear data">
            <ClearDBIcon
              style={{ fontSize: 16 }}
              onClick={() => {
                renderConfirmModal(
                  `Are you sure you want to clear the test data for "${record.name}" ?`,
                  async () => {
                    handleArchiveEvents(record.id)
                  },
                )
              }}
            />
          </Tooltip>
          {canUpdateTests ? (
            <Tooltip title="Edit test">
              <Link to={`/tests/${record.id}/edit`}>
                <EditIcon style={{ fontSize: 16, color: 'white' }} />
              </Link>
            </Tooltip>
          ) : null}
          <Tooltip title="Metabase report">
            <ReportIcon style={{ fontSize: 16, cursor: 'pointer' }} />
          </Tooltip>
          {canUpdateTests ? (
            <Tooltip
              title={
                record.status === 'archived' ? 'Delete test' : 'Archive test'
              }
            >
              <DeleteIcon
                style={{ fontSize: 16, color: 'white', cursor: 'pointer' }}
                onClick={() => {
                  renderConfirmModal(
                    `Are you sure you want to ${record.status === 'archived' ? 'delete' : 'archive'} the test "${record.name}"?`,
                    async () =>
                      record.status === 'archived'
                        ? handleDelete(record.id)
                        : handleArchive(record.id),
                  )
                }}
              />
            </Tooltip>
          ) : null}
        </Flex>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <Title level={4}>A/B Tests</Title>
        <p style={{ color: '#ff4d4f' }}>{error}</p>
        <Button
          onClick={() =>
            fetchTests({
              page: 1,
              pageSize: pagination.pageSize,
              sort: sortField,
              status: statusFilter,
              search,
            })
          }
        >
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div>
      <style>
        {/* overwrites inline because I'm tired of fighting ant */}
        {`
        .ant-table-tbody .ant-table-cell {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          border: thin solid #e5e8ec !important;
        }
        .ant-table-tbody tr td:nth-of-type(3) {
          border-left: none !important;
        }
      `}
      </style>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          A/B Tests
        </Title>
        <Flex gap="small" align="center">
          <SearchInput onSearch={handleSearch} />
          <Button
            onClick={() => {
              const next = statusFilter === 'archived' ? undefined : 'archived'
              setStatusFilter(next)
              fetchTests({
                page: 1,
                pageSize: pagination.pageSize,
                sort: sortField,
                status: next,
                search,
              })
            }}
          >
            {statusFilter === 'archived' ? 'View Active' : 'View Archived'}
          </Button>
          {canCreateTests ? (
            <Button type="primary" onClick={() => navigate('/tests/new')}>
              New Test
            </Button>
          ) : null}
        </Flex>
      </Flex>
      {canReadTests ? (
        <Spin spinning={loading}>
          <Table<Test>
            rowKey="id"
            columns={columns}
            dataSource={tests}
            pagination={pagination}
            className={`${modalOpen || confirmModalContent ? styles['blur'] : ''} ${styles['ant-table-overwrite']}`}
            onChange={(p, filters, sorter) => {
              const sort = resolveBackendSort(
                sorter,
                columnSortKeys,
                '-created_at',
              )
              const status = filters.status?.[0] as string | undefined
              setSortField(sort)
              setStatusFilter(status)
              fetchTests({
                page: p.current ?? 1,
                pageSize: p.pageSize ?? pagination.pageSize,
                sort,
                status,
                search,
              })
            }}
          />
        </Spin>
      ) : null}
      <Modal
        modalOpen={modalOpen}
        modalData={modalData}
        formatter={metricFMT}
        colorRenderer={metricColor}
        closeModal={() => {
          setModalOpen(false)
        }}
      />
      {confirmModalContent && <>{confirmModalContent}</>}
    </div>
  )
}
